/**
 * DataState — smart wrapper that picks the right `<DataStateCard>` for
 * the current async state, or renders `children` if data is ready.
 *
 * V2.I.6 motivation: pages across the app reimplement the
 * `loading / error / empty / data` 4-state machine inline, with
 * inconsistent visuals (some pages show a centred spinner, some show
 * nothing at all on error, some have a unique empty-state design).
 * Wrapping the data-rendering branch in `<DataState>` collapses all
 * 4 cases into one declarative component and forces consistency.
 *
 * Branch order (first match wins):
 *  1. `error` truthy           → DataStateCard variant="error" + retry
 *  2. `loading` truthy         → DataStateCard variant="loading"
 *  3. `isEmpty` truthy         → DataStateCard variant="empty"
 *  4. otherwise                → `children`
 *
 * All slots are optional — leave `errorIcon` / `loadingIcon` /
 * `emptyIcon` undefined to fall back to the per-state default. Same
 * for messages.
 *
 * Example :
 *   <DataState
 *     loading={isLoading}
 *     error={syncError}
 *     onRetry={refresh}
 *     isEmpty={alerts.length === 0}
 *     emptyTitle="Aucune alerte"
 *     emptyMessage="Créez-en une pour commencer."
 *     emptyAction={<button onClick={openCreateForm}>Créer</button>}
 *   >
 *     <AlertsGrid alerts={alerts} />
 *   </DataState>
 */

import type { ReactNode } from 'react';
import { Icons } from '../Icons';
import { DataStateCard } from './DataStateCard';

interface DataStateProps {
  /** True while the underlying fetch is in flight. */
  loading?: boolean;
  /** Truthy when the fetch failed. Accepts an Error or a string. */
  error?: Error | string | null | undefined;
  /** True when data is loaded but the result set is empty. */
  isEmpty?: boolean;
  /** Children rendered when none of the above match. */
  children: ReactNode;

  // Loading slot
  loadingIcon?: ReactNode;
  loadingMessage?: string;

  // Error slot
  errorIcon?: ReactNode;
  errorTitle?: string;
  /** Custom error message. If omitted, falls back to the error itself. */
  errorMessage?: string;
  /** Retry callback — surfaces a button on the error card. */
  onRetry?: () => void;
  retryLabel?: string;

  // Empty slot
  emptyIcon?: ReactNode;
  emptyTitle?: string;
  emptyMessage?: string;
  /** Optional CTA on the empty card (e.g. "Create your first item"). */
  emptyAction?: ReactNode;

  /** Semantic heading level for the empty/error title. Default `h3`.
   *  Pass `h2` when this DataState lives directly under a page-level
   *  `h1` (avoids heading-order a11y warnings). */
  headingLevel?: 'h2' | 'h3' | 'h4';
}

export function DataState({
  loading,
  error,
  isEmpty,
  children,
  loadingIcon,
  loadingMessage = 'Chargement…',
  errorIcon,
  errorTitle = 'Une erreur est survenue',
  errorMessage,
  onRetry,
  retryLabel,
  emptyIcon,
  emptyTitle = 'Aucune donnée',
  emptyMessage = 'Aucun élément à afficher pour le moment.',
  emptyAction,
  headingLevel,
}: DataStateProps) {
  // Branch 1 — error wins (we want to surface failures even if we have stale data).
  if (error) {
    const detail =
      errorMessage ?? (typeof error === 'string' ? error : error?.message ?? errorTitle);
    return (
      <DataStateCard
        variant="error"
        icon={errorIcon ?? <Icons.AlertCircle size={48} />}
        title={errorTitle}
        message={detail}
        onRetry={onRetry}
        retryLabel={retryLabel}
        headingLevel={headingLevel}
      />
    );
  }

  // Branch 2 — loading.
  if (loading) {
    return (
      <DataStateCard
        variant="loading"
        icon={loadingIcon ?? <Icons.Activity size={48} />}
        message={loadingMessage}
      />
    );
  }

  // Branch 3 — empty.
  if (isEmpty) {
    return (
      <DataStateCard
        variant="empty"
        icon={emptyIcon ?? <Icons.Inbox size={48} />}
        title={emptyTitle}
        message={emptyMessage}
        action={emptyAction}
        headingLevel={headingLevel}
      />
    );
  }

  // Branch 4 — data ready.
  return <>{children}</>;
}
